import { useCallback, useEffect, useState } from 'react';
import { Card, DataTable, Modal, FormField, TextInput, StatusBadge, ConfirmDialog } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type ApiScopeMeta, type HubApiKey } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { fmtDateTime, SecretModal } from './common';

const USAGE_EXAMPLE = `curl https://api.kailai.tech/api/v1/merkaziya/v1/contacts \\
  -H "X-Api-Key: YOUR_KEY"`;

/**
 * שמות ההרשאות בעברית — fallback כשהשרת לא מחזיר scope_catalog (גרסה ישנה).
 * מקור האמת: ApiScopes::CATALOG בשרת.
 */
const SCOPE_FALLBACK: Record<string, { label: string; group: string }> = {
  'leads.read': { label: 'קריאת לידים', group: 'לידים וקמפיינים' },
  'leads.write': { label: 'יצירה ועדכון לידים', group: 'לידים וקמפיינים' },
  'campaigns.read': { label: 'קריאת קמפיינים', group: 'לידים וקמפיינים' },
  'campaigns.write': { label: 'הפעלה והשהיה של קמפיינים', group: 'לידים וקמפיינים' },
  'contacts.read': { label: 'קריאת אנשי קשר', group: 'אנשי קשר' },
  'contacts.write': { label: 'יצירה ועדכון אנשי קשר', group: 'אנשי קשר' },
  'calls.read': { label: 'קריאת שיחות', group: 'שיחות' },
  'calls.originate': { label: 'חיוג יוצא', group: 'שיחות' },
  'recordings.read': { label: 'קריאת הקלטות', group: 'שיחות' },
  'callbacks.write': { label: 'קביעת שיחות חוזרות', group: 'שיחות' },
  'agents.read': { label: 'קריאת נציגים', group: 'נציגים' },
  'agents.write': { label: 'שינוי זמינות נציגים', group: 'נציגים' },
  'extensions.read': { label: 'קריאת שלוחות', group: 'נציגים' },
  'blocks.read': { label: 'קריאת חסימות', group: 'חסימות' },
  'blocks.write': { label: 'ניהול חסימות', group: 'חסימות' },
  'automations.trigger': { label: 'הפעלת אוטומציות', group: 'אוטומציות' },
};

function scopeMeta(catalog: ApiScopeMeta[], key: string): ApiScopeMeta {
  return catalog.find((c) => c.key === key)
    ?? { key, label: SCOPE_FALLBACK[key]?.label ?? key, description: '', group: SCOPE_FALLBACK[key]?.group ?? 'אחר' };
}

export function ApiKeysTab() {
  const [rows, setRows] = useState<HubApiKey[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<ApiScopeMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<HubApiKey | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    adminApi.hubApiKeys()
      .then((r) => { setRows(r.data); setScopes(r.available_scopes); setCatalog(r.scope_catalog); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function del(k: HubApiKey) {
    try {
      await adminApi.deleteHubApiKey(k.id);
      toast('נמחק', 'ok');
      setConfirmDel(null);
      load();
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const cols: DataTableColumn<HubApiKey>[] = [
    { key: 'name', he: 'שם', render: (r) => r.name || '—' },
    { key: 'prefix', he: 'קידומת', render: (r) => <code dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>{r.prefix}…</code> },
    {
      key: 'scopes', he: 'הרשאות', render: (r) => (
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(r.scopes ?? []).length === 0 || (r.scopes ?? []).includes('*')
            ? <StatusBadge tone="warn" size="sm">גישה מלאה</StatusBadge>
            : (r.scopes ?? []).map((s) => <StatusBadge key={s} tone="info" size="sm"><span title={s}>{scopeMeta(catalog, s).label}</span></StatusBadge>)}
        </span>
      ),
    },
    { key: 'last_used_at', he: 'שימוש אחרון', render: (r) => fmtDateTime(r.last_used_at) },
    { key: 'expires_at', he: 'תוקף', render: (r) => (r.expires_at ? fmtDateTime(r.expires_at) : 'ללא') },
    {
      key: '_actions', he: '', render: (r) => (
        <button type="button" className="btn ghost" onClick={(e) => { e.stopPropagation(); setConfirmDel(r); }} aria-label="מחיקה"><Icon name="trash" size={14} /></button>
      ),
    },
  ];

  return (
    <div className="stack">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ מפתח חדש</button>
      </div>

      {loading ? (
        <Card>טוען…</Card>
      ) : error ? (
        <Card><div className="stack"><span style={{ color: 'var(--danger)' }}>שגיאה בטעינה.</span><button type="button" className="btn" onClick={load}>נסה שוב</button></div></Card>
      ) : (
        <Card padded={false}>
          <DataTable<HubApiKey> columns={cols} rows={rows} emptyMessage="אין מפתחות API." />
        </Card>
      )}

      <Card title="דוגמת שימוש">
        <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)' }}>
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>cURL</span>
            <button
              type="button"
              className="btn ghost"
              style={{ height: 24, padding: '0 6px' }}
              onClick={() => { void navigator.clipboard.writeText(USAGE_EXAMPLE).then(() => toast('הועתק', 'ok')).catch(() => {}); }}
              title="העתק"
              aria-label="העתק דוגמה"
            >
              <Icon name="copy" size={13} />
            </button>
          </div>
          <pre
            dir="ltr"
            style={{
              margin: 0, padding: 12, background: '#0f172a', color: '#e2e8f0',
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', lineHeight: 1.5,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto',
            }}
          >
            {USAGE_EXAMPLE}
          </pre>
        </div>
      </Card>

      {createOpen && (
        <CreateKeyModal
          scopes={scopes}
          catalog={catalog}
          onClose={() => setCreateOpen(false)}
          onCreated={(key) => { setCreateOpen(false); setNewKey(key); load(); }}
        />
      )}

      {newKey && <SecretModal title="מפתח API חדש" label="API Key" secret={newKey} onClose={() => setNewKey(null)} />}

      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={() => { if (confirmDel) void del(confirmDel); }}
        title="מחיקת מפתח"
        message={`למחוק את "${confirmDel?.name}"? כל שימוש במפתח יפסיק לעבוד.`}
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}

function CreateKeyModal({ scopes, catalog, onClose, onCreated }: { scopes: string[]; catalog: ApiScopeMeta[]; onClose: () => void; onCreated: (key: string) => void }) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  function toggle(s: string) {
    setSelected((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  // ההרשאות מקובצות לפי תחום, עם שם ותיאור בעברית; המפתח הטכני נשאר קטן לצד.
  const groups = scopes.reduce<Record<string, ApiScopeMeta[]>>((acc, key) => {
    const m = scopeMeta(catalog, key);
    (acc[m.group] ??= []).push(m);
    return acc;
  }, {});
  const toggleGroup = (keys: string[], on: boolean) => setSelected((cur) => on
    ? Array.from(new Set([...cur, ...keys]))
    : cur.filter((x) => !keys.includes(x)));

  async function save() {
    if (!name.trim()) { toast('יש להזין שם', 'danger'); return; }
    setSaving(true);
    try {
      const r = await adminApi.createApiKey(name.trim(), selected, expiresAt || null);
      toast('המפתח נוצר', 'ok');
      onCreated(r.key);
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה ביצירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="מפתח API חדש"
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'יוצר…' : 'צור'}</button>
      </>}
    >
      <div className="stack">
        <FormField label="שם" required><TextInput value={name} onChange={(e) => setName(e.target.value)} /></FormField>
        <FormField label="הרשאות" hint={selected.length === 0 ? 'בלי בחירה המפתח מקבל גישה מלאה לכל ה-API. מומלץ לסמן רק מה שהמערכת החיצונית צריכה.' : `${selected.length} הרשאות נבחרו`}>
          {scopes.length === 0 ? (
            <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין הרשאות זמינות.</span>
          ) : (
            <div className="stack" style={{ gap: 10 }} data-testid="scope-groups">
              {Object.entries(groups).map(([group, items]) => {
                const keys = items.map((i) => i.key);
                const allOn = keys.every((k) => selected.includes(k));
                return (
                  <div key={group} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: '8px 10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong style={{ fontSize: 'var(--t-sm)' }}>{group}</strong>
                      <button type="button" className="btn ghost btn-sm" onClick={() => toggleGroup(keys, !allOn)}>{allOn ? 'נקה' : 'בחר הכל'}</button>
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {items.map((m) => (
                        <label key={m.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={selected.includes(m.key)} onChange={() => toggle(m.key)} aria-label={m.label} style={{ marginTop: 3 }} />
                          <span>
                            <span style={{ fontWeight: 600 }}>{m.label}</span>
                            <code dir="ltr" style={{ marginInlineStart: 6, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{m.key}</code>
                            {m.description && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{m.description}</div>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </FormField>
        <FormField label="תוקף (אופציונלי)">
          <TextInput type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} dir="ltr" />
        </FormField>
      </div>
    </Modal>
  );
}
